"use client"

import styled from "@emotion/styled"
import { extractProperty } from "@eurodrive/utils"
import { calculatorUsed } from "@eurodrive/utils/gtm"
import { Button, ClickAwayListener, Tooltip, Typography } from "@mui/material"
import Link from "next/link"
import { useEffect, useState } from "react"
import Container from "./Container"
import Input from "./Input"
import { sharedColors } from "./theme/colors"

const StyledContainer = styled.div`
  padding-top: 40px;
  padding-bottom: 64px;
  display: flex;
  flex-direction: column;
  gap: 24px;
  ${({ theme }) => theme.breakpoints.up("md")} {
    padding-top: 64px;
  }
`

const MainContainer = styled.div`
  display: grid;
  gap: 24px;
  grid-template-columns: 1fr 327px;
  ${({ theme }) => theme.breakpoints.down("md")} {
    grid-template-columns: 1fr;
  }
`

const InputContainer = styled.div`
  display: flex;
  width: 100%;
  padding: 40px;
  flex-direction: column;
  gap: 24px;
  border-radius: 8px;
  border: 1px solid ${sharedColors.lightGrey};
`

const ResultContainer = styled.div`
  display: flex;
  width: 100%;
  padding: 40px;
  flex-direction: column;
  justify-content: space-between;
  align-items: center;
  gap: 76px;
  border-radius: 8px;
  background-color: ${sharedColors.lightGrey};
`

const DetailsColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 100%;
`

const DetailRow = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
`

const TooltipContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`

/*
 * The glyph stays 16px, but the button itself is padded out to a 44px touch target
 * and the padding pulled back off the layout with a matching negative margin
 * (END-287). A bare 16px target is smaller than a fingertip, so a tap landing a few
 * pixels off the icon hit the row behind it and nothing opened. The negative margin
 * cancels the padding, so the button still measures 16px to the surrounding flex row
 * and the results table is laid out exactly as before.
 */
const ICON_SIZE = 16
const TOUCH_TARGET = 44
const HIT_PADDING = (TOUCH_TARGET - ICON_SIZE) / 2

const InfoIcon = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  box-sizing: content-box;
  width: ${ICON_SIZE}px;
  height: ${ICON_SIZE}px;
  cursor: pointer;
  background: none;
  border: none;
  padding: ${HIT_PADDING}px;
  margin: -${HIT_PADDING}px;
  /* Suppress the double-tap-to-zoom wait so the tap registers immediately. */
  touch-action: manipulation;
  -webkit-tap-highlight-color: transparent;
  filter: brightness(0) saturate(100%) invert(18%) sepia(30%) saturate(1800%)
    hue-rotate(92deg) brightness(95%) contrast(95%);
  transition: opacity 0.2s;

  &:hover,
  &:focus {
    opacity: 0.8;
  }

  &:focus-visible {
    border: 1px dashed white;
    outline-offset: 2px;
    outline: 2px solid black;
  }
`

const disclaimerTooltipProps = {
  tooltip: {
    sx: {
      bgcolor: "#ffffff",
      color: "#000000",
      border: "1px solid #ece9e9",
      borderRadius: "8px",
      padding: "12px",
      maxWidth: "316px",
      boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.1)",
      "& .MuiTooltip-arrow": {
        color: "#ffffff",
        "&::before": {
          border: "1px solid #ece9e9",
        },
      },
    },
  },
}

/*
 * Reports whether the device has no hover, which is what separates the two disclaimer
 * behaviours below (END-287).
 *
 * Starts false so the server render and the first client render agree, then corrects on
 * mount. A pointer device is the safe starting assumption: it renders the tooltip the
 * way it has always behaved, so a hydration pass that never reached the effect would
 * degrade to today's behaviour rather than to something new.
 */
function useIsTouchDevice() {
  const [isTouch, setIsTouch] = useState(false)

  useEffect(() => {
    const query = window.matchMedia("(hover: none)")
    setIsTouch(query.matches)

    const handleChange = (event: MediaQueryListEvent) =>
      setIsTouch(event.matches)
    query.addEventListener("change", handleChange)
    return () => query.removeEventListener("change", handleChange)
  }, [])

  return isTouch
}

const Divider = styled.div`
  width: 100%;
  height: 1px;
  background-color: ${sharedColors.mediumGrey};
  margin: 12px 0;
`

const StyledButton = styled(Button)`
  width: 100%;
  margin-top: auto;
`
function numberFormat(value: number) {
  return (
    new Intl.NumberFormat("he-IL", {
      style: "decimal",
    }).format(value) + " ש״ח"
  )
}

export default function CalculatorContent({
  data,
  fixedPrice,
  noContainer,
  title,
}: {
  data: any
  fixedPrice?: string
  noContainer?: boolean
  title?: string
}) {
  const legalText = extractProperty(data, "legalText")
  const amountOfTheDownPayment = extractProperty(data, "amountOfTheDownPayment")
  const maximumNumberOfPayments = extractProperty(
    data,
    "maximumNumberOfPayments",
  )
  const weightedInterest = extractProperty(data, "weightedInterest")

  // ------------------------------
  // Helpers
  // ------------------------------
  const parsePercent = (str: string | null | undefined): number => {
    if (!str) return 0
    return parseFloat(String(str).replace(/[^0-9.]/g, "")) / 100
  }

  // Maximum monthly payment equals the maximum financeable principal (car price − minimum required down-payment)
  const minDpPercent = parsePercent(amountOfTheDownPayment) // e.g. 0.1

  const getMaxMonthlyPayment = (price: number) =>
    price > 0 ? price * (1 - minDpPercent) : 15_000 // fallback when car price not yet entered

  // Annual interest percentage → monthly rate (dec). Standard compounding.
  const annualRate = parsePercent(weightedInterest) // e.g. 0.1099
  const monthlyRate = Math.pow(1 + annualRate, 1 / 12) - 1

  // Loan formulas
  const calcMonthlyPayment = (principal: number, months: number): number => {
    if (months <= 0) return 0
    const r = monthlyRate
    if (r <= 0) return principal / months
    return (principal * r) / (1 - Math.pow(1 + r, -months))
  }

  // Given desired payment, derive required principal
  const principalFromPayment = (payment: number, months: number): number => {
    const r = monthlyRate
    if (r <= 0) return payment * months
    return (payment * (1 - Math.pow(1 + r, -months))) / r
  }

  // ------------------------------
  // State
  // ------------------------------
  const initialCarPrice = fixedPrice ? parseFloat(fixedPrice) : 0
  const initialMinDownPayment = initialCarPrice * minDpPercent
  const [formValues, setFormValues] = useState({
    carPrice: initialCarPrice,
    downPaymentAmount: initialMinDownPayment,
    numPayments: 0,
    monthlyPayment: Math.min(
      calcMonthlyPayment(initialCarPrice - initialMinDownPayment, 0),
      getMaxMonthlyPayment(initialCarPrice),
    ),
  })

  // ensure monthlyPayment initialised based on above
  const minDownPayment = Math.max(formValues.carPrice * minDpPercent, 0)

  const isTouchDevice = useIsTouchDevice()
  const [isDisclaimerOpen, setIsDisclaimerOpen] = useState(false)

  // ------------------------------
  // Handlers
  // ------------------------------
  const updateState = (partial: Partial<typeof formValues>) =>
    setFormValues((prev) => ({ ...prev, ...partial }))

  const handleCarPrice = (val: number) => {
    const price = Math.max(val, 0)
    const minDp = price * minDpPercent
    const dp = Math.min(Math.max(formValues.downPaymentAmount, minDp), price)
    const newMonthly = calcMonthlyPayment(price - dp, formValues.numPayments)
    updateState({
      carPrice: price,
      downPaymentAmount: dp,
      monthlyPayment: Math.min(newMonthly, getMaxMonthlyPayment(price)),
    })
    calculatorUsed(
      String(price),
      weightedInterest || "0%",
      String(formValues.numPayments),
    )
  }

  const handleDownPayment = (val: number) => {
    const minDp = formValues.carPrice * minDpPercent
    // Allow the value to be set even if it's below min or above max
    // The Input component will clamp it when the user is done typing (onBlur)
    const dp = Math.max(0, Math.min(val, formValues.carPrice))
    const principal = Math.max(0, formValues.carPrice - dp)
    const newMonthly = calcMonthlyPayment(principal, formValues.numPayments)
    updateState({
      downPaymentAmount: dp,
      monthlyPayment: Math.min(
        newMonthly,
        getMaxMonthlyPayment(formValues.carPrice),
      ),
    })
    calculatorUsed(
      String(formValues.carPrice - dp),
      weightedInterest || "0%",
      String(formValues.numPayments),
    )
  }

  const handleNumPayments = (val: number) => {
    const months = Math.min(
      Math.max(val, 1),
      Number(maximumNumberOfPayments) || 120,
    )
    const newMonthly = calcMonthlyPayment(
      formValues.carPrice - formValues.downPaymentAmount,
      months,
    )
    updateState({
      numPayments: months,
      monthlyPayment: Math.min(
        newMonthly,
        getMaxMonthlyPayment(formValues.carPrice),
      ),
    })
    calculatorUsed(
      String(formValues.carPrice - formValues.downPaymentAmount),
      weightedInterest || "0%",
      String(months),
    )
  }

  const handleMonthlyPayment = (val: number) => {
    const desiredPayment = Math.min(
      Math.max(val, 0),
      getMaxMonthlyPayment(formValues.carPrice),
    )
    // If number of payments is not set yet, just update the value directly
    if (formValues.numPayments <= 0) {
      updateState({ monthlyPayment: desiredPayment })
      return
    }
    const principalNeeded = principalFromPayment(
      desiredPayment,
      formValues.numPayments,
    )
    let newDp = formValues.carPrice - principalNeeded
    const minDp = formValues.carPrice * minDpPercent
    if (newDp < minDp) {
      newDp = minDp
    }
    if (newDp > formValues.carPrice) {
      newDp = formValues.carPrice
    }
    const newMonthly = calcMonthlyPayment(
      formValues.carPrice - newDp,
      formValues.numPayments,
    )
    updateState({
      downPaymentAmount: newDp,
      monthlyPayment: Math.min(
        newMonthly,
        getMaxMonthlyPayment(formValues.carPrice),
      ),
    })
    calculatorUsed(
      String(formValues.carPrice - newDp),
      weightedInterest || "0%",
      String(formValues.numPayments),
    )
  }
  const renderContent = () => (
    <StyledContainer>
      <MainContainer>
        <InputContainer
          role="group"
          aria-labelledby="calculator-inputs-heading"
        >
          <Typography
            id="calculator-inputs-heading"
            variant="h2"
            className="sr-only"
          >
            הזן פרטי הלוואה
          </Typography>
          <Input
            label="מחיר הרכב"
            placeholder="000,000"
            value={formValues.carPrice}
            onChange={handleCarPrice}
            min={0}
            max={999_999}
            currency
            disabled={!!fixedPrice}
          />
          <Input
            label="גובה המקדמה"
            placeholder="0"
            value={formValues.downPaymentAmount}
            onChange={handleDownPayment}
            min={minDownPayment}
            max={formValues.carPrice}
            currency
          />
          <Input
            label="מספר התשלומים"
            placeholder="0"
            value={formValues.numPayments}
            onChange={handleNumPayments}
            min={0}
            max={Number(maximumNumberOfPayments) || 120}
            months
          />
          <Input
            label="סכום ההחזר החודשי"
            placeholder="0"
            value={formValues.monthlyPayment}
            onChange={handleMonthlyPayment}
            min={0}
            max={getMaxMonthlyPayment(formValues.carPrice)}
            currency
            disabled
          />
        </InputContainer>
        <ResultContainer>
          <Typography id="calculator-results-heading" variant="h2">
            {title || "תוצאות החישוב"}
          </Typography>
          <DetailsColumn>
            <DetailRow>
              <Typography variant="body2">ריבית משוקללת</Typography>
              <TooltipContainer>
                <Typography variant="body2">{weightedInterest}</Typography>
                {/*
                 * Touch drives the disclaimer from a click; pointer devices keep MUI's
                 * hover behaviour untouched (END-287).
                 *
                 * MUI's touch path was unusable for this: it waits out a 700ms long
                 * press before opening, so an ordinary tap never opened the disclaimer.
                 * Shortening that delay is not enough on its own — the path has no
                 * touchmove cancellation, so a pan starting over the icon opens the
                 * tooltip too, and a timer is its only close path, which leaves the
                 * panel sitting over the results with nothing able to dismiss it.
                 *
                 * Driving it from click instead gives touch a real toggle and a real
                 * dismiss, and a pan produces no click so it cannot open by accident.
                 * MUI's own hover and touch listeners are off in that branch because a
                 * tap also emits a synthetic mouseover, and the hover would otherwise
                 * open what the tap had just closed.
                 */}
                {isTouchDevice ? (
                  <ClickAwayListener
                    onClickAway={() => setIsDisclaimerOpen(false)}
                    touchEvent="onTouchStart"
                  >
                    <Tooltip
                      title={
                        <Typography variant="body2">
                          {legalText || ""}
                        </Typography>
                      }
                      arrow
                      placement="bottom"
                      open={isDisclaimerOpen}
                      disableHoverListener
                      disableTouchListener
                      disableFocusListener
                      componentsProps={disclaimerTooltipProps}
                    >
                      <InfoIcon
                        type="button"
                        aria-label="מידע נוסף על ריבית משוקללת"
                        aria-expanded={isDisclaimerOpen}
                        onClick={() => setIsDisclaimerOpen((isOpen) => !isOpen)}
                      >
                        <img
                          src="/icons/info-tooltip.svg"
                          alt="מידע נוסף"
                          aria-hidden="true"
                          width={ICON_SIZE}
                          height={ICON_SIZE}
                        />
                      </InfoIcon>
                    </Tooltip>
                  </ClickAwayListener>
                ) : (
                  <Tooltip
                    title={
                      <Typography variant="body2">{legalText || ""}</Typography>
                    }
                    arrow
                    placement="bottom"
                    componentsProps={disclaimerTooltipProps}
                  >
                    <InfoIcon
                      type="button"
                      aria-label="מידע נוסף על ריבית משוקללת"
                    >
                      <img
                        src="/icons/info-tooltip.svg"
                        alt="מידע נוסף"
                        aria-hidden="true"
                        width={ICON_SIZE}
                        height={ICON_SIZE}
                      />
                    </InfoIcon>
                  </Tooltip>
                )}
              </TooltipContainer>
            </DetailRow>
            <Divider />
            <DetailRow>
              <Typography variant="body2">מחיר הרכב</Typography>
              <Typography variant="body2">
                {numberFormat(formValues.carPrice)}
              </Typography>
            </DetailRow>
            <DetailRow>
              <Typography variant="body2">מקדמה</Typography>
              <Typography variant="body2">
                {numberFormat(formValues.downPaymentAmount)}
              </Typography>
            </DetailRow>
            <DetailRow>
              <Typography variant="body2">מספר תשלומים</Typography>
              <Typography variant="body2">{formValues.numPayments}</Typography>
            </DetailRow>
            <Divider />
            <DetailRow>
              <Typography variant="body2">סה״כ סכום מימון</Typography>
              <Typography variant="body2">
                {numberFormat(
                  formValues.carPrice - formValues.downPaymentAmount,
                )}
              </Typography>
            </DetailRow>
            <DetailRow>
              <Typography variant="body2">תשלום חודשי </Typography>
              <Typography variant="h3" aria-live="polite" aria-atomic="true">
                {numberFormat(formValues.monthlyPayment)}
              </Typography>
            </DetailRow>
          </DetailsColumn>
          <StyledButton
            variant="primary"
            size="large"
            LinkComponent={Link}
            href="/contact-us"
          >
            צור קשר
          </StyledButton>
        </ResultContainer>
      </MainContainer>
      <Typography variant="h3" component="h3">
        הערות משפטיות
      </Typography>
      <Typography variant="body3" color={sharedColors.mediumGrey}>
        {legalText}
      </Typography>
    </StyledContainer>
  )
  if (noContainer) {
    return <>{renderContent()}</>
  }
  return <Container paddingMobile>{renderContent()}</Container>
}
